import { useContext, useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Alert, Flex, Spacer } from '@zillow/constellation';
import { Card, Text, TextButton } from '@zillow/constellation-10';

import { useGetListingPrices, useGetPremiumOverview } from '../../../../hooks/queries/freemium';
import { AppSelectors, RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import stringHelper from '../../../../utils/stringHelper';
import { FreemiumContext } from '../../../../components/Freemium/context';
import SpinnerContainer from '../../../SpinnerContainer';
import { COLORS } from '../../interfaces';
import ActiveCompsTooltip from '../../../../components/Freemium/components/ActiveCompsTooltip';
import NotEnoughComps from '../../../../components/Freemium/components/NotEnoughComps';

import CustomLabel from './components/CustomLabel';
import Filters from './components/Filters';
import { StyledCircle, StyledFlexEndLegend, StyledLegend, StyledMiddleLegendSpacer } from '../StyledComponents';
import { StyledContainerGrid, StyledGrid } from '../styles';

import type { InquiryPayload } from '../../interfaces';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

type RentPricingChartProps = {
  isInModal: boolean;
};

const RentPricingChart = ({ isInModal }: RentPricingChartProps) => {
  const {
    freemiumState: {
      filters: { beds, filterType, propertyType },
    },
  } = useContext(FreemiumContext);

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const {
    data: pricingData,
    isLoading,
    error,
    refetch: refetchGetListingPrices,
  } = useGetListingPrices(
    { listingId, beds, filterType, propertyType },
    {
      keepPreviousData: true,
    },
  );

  const { data: overviewData } = useGetPremiumOverview({ listingId });

  const propertyPrice: number | null = useMemo(
    () =>
      typeof pricingData?.property?.price !== 'undefined' ? Number(pricingData?.property?.price.substring(1)) : null,
    [pricingData?.property?.price],
  );

  if (!overviewData || !pricingData) {
    return null;
  }

  if (isLoading) {
    return (
      <StyledContainerGrid
        includeWhiteBackground={true}
        css={{
          minHeight: { base: '680px', xl: '436px' },
          padding: '1200',
          gridTemplateColumns: 'repeat(1, 1fr)',
        }}
      >
        <Flex display="flex" justifyContent="center" alignContent="center">
          <SpinnerContainer height="auto" />
        </Flex>
      </StyledContainerGrid>
    );
  }

  if (error) {
    return (
      <Alert
        appearance="warning"
        body={
          <Text asChild>
            <p>
              We are having issues loading your market pricing data.
              <TextButton onClick={() => refetchGetListingPrices()}>&nbsp;Retry now.</TextButton>
            </p>
          </Text>
        }
      />
    );
  }

  const renderTooltip = ({ payload }: TooltipProps<ValueType, NameType>) => {
    const { price } = (payload?.[0]?.payload as InquiryPayload) || {};
    return payload && price ? (
      <Card outlined={false} elevated={true} tone="neutral">
        <Text textStyle="body-xs-bold" asChild>
          <p>{price.includes('-') || price.includes('+') ? price : `${price} - $${Number(price.substring(1)) + 99}`}</p>
        </Text>
        <Text textStyle="body-xs">{`${stringHelper.pluralize(
          payload[0]?.payload?.count,
          'listing',
          'listings',
        )}`}</Text>
      </Card>
    ) : null;
  };

  const renderLegend = () => {
    const shouldDisplayPropertyPriceFirst =
      propertyPrice !== null && pricingData?.property?.price < pricingData?.marketMedian?.price;
    const propertyKey = (
      <>
        <StyledCircle color={COLORS.BLUE500} />
        <Text textStyle="body-sm" css={{ marginInline: '200', color: 'text.subtle' }}>
          Your listing
        </Text>
        <Text textStyle="body-sm-bold" asChild>
          <span>{pricingData?.property?.price}</span>
        </Text>
      </>
    );

    const marketMedianKey = (
      <>
        <StyledCircle color={COLORS.YELLOW400} />
        <Spacer marginX="xs">
          <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
            <ActiveCompsTooltip triggerText="Median of comparable listings" />
          </Text>
        </Spacer>
        <Text textStyle="body-sm-bold" asChild>
          <span>{pricingData?.marketMedian?.price}</span>
        </Text>
      </>
    );
    return (
      <StyledLegend
        isInModal={isInModal}
        paddingBottom={0.5}
        display="flex"
        flexDirection={{ default: 'column', xxl: 'row' }}
      >
        <Flex display="flex" alignItems="center">
          {shouldDisplayPropertyPriceFirst ? propertyKey : marketMedianKey}
        </Flex>
        <StyledMiddleLegendSpacer>
          {propertyPrice !== null && (
            <Flex display="flex" alignItems="center">
              {shouldDisplayPropertyPriceFirst ? marketMedianKey : propertyKey}
            </Flex>
          )}
        </StyledMiddleLegendSpacer>
        <StyledFlexEndLegend>
          <TextButton asChild tone="subtle" textStyle="body-sm">
            <a target="_blank" rel="noreferrer" href={overviewData.compsUrl}>
              Source: active comparable listings
            </a>
          </TextButton>
        </StyledFlexEndLegend>
      </StyledLegend>
    );
  };

  return (
    <StyledGrid
      css={{
        gridColumnEnd: { base: 'span 4', xl: 'span 2' },
      }}
    >
      {pricingData?.hasActiveComps ? (
        <>
          <Flex
            display="flex"
            justifyContent={isInModal ? 'space-between' : 'flex-start'}
            flexDirection={{ default: 'column', xl: 'row' }}
            alignItems={{ default: 'flex-start', xl: 'center' }}
          >
            <Text
              css={{
                textStyle: { base: 'body-sm-bold', xl: 'body-sm' },
                marginBottom: { base: '400', xl: '600' },
              }}
            >
              Number of <ActiveCompsTooltip triggerText="active comparable listings" /> by monthly rent
            </Text>
            {isInModal && <Filters />}
          </Flex>
          <ResponsiveContainer width="99%" height={300}>
            <ComposedChart margin={{ left: -20 }} height={400} data={pricingData?.prices}>
              <XAxis
                dataKey="price"
                padding={{ left: 10 }}
                tickLine={false}
                style={{ fill: COLORS.TEXT_MEDIUM, fontSize: '12px' }}
              />
              <YAxis
                allowDecimals={false}
                tickCount={5}
                type="number"
                tickLine={false}
                stroke="#FFF"
                style={{ fill: COLORS.TEXT_MEDIUM, fontSize: '14px' }}
              />
              <Tooltip content={renderTooltip} />
              <Legend content={() => renderLegend()} />
              <CartesianGrid horizontal={true} vertical={false} stroke={COLORS.GRAY200} />
              <Bar
                animationDuration={0}
                isAnimationActive={false}
                dataKey="count"
                name="Your property"
                barSize={isScreenDesktop ? 20 : 12}
                fill={COLORS.LIGHTBLUE}
              >
                <LabelList
                  dataKey="labelListDataKey"
                  color={COLORS.BLUE500}
                  position="insideTop"
                  // @ts-expect-error
                  content={<CustomLabel />}
                />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </>
      ) : (
        <NotEnoughComps />
      )}
    </StyledGrid>
  );
};

export default RentPricingChart;
